import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { Observable, combineLatest, BehaviorSubject, Subscription } from 'rxjs';
import { map, startWith, shareReplay, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { Template } from '@workspaces/models/templates';
import { templateFeature, TemplateFetch } from '@workspaces/store/template.feature';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TemplateCardComponent } from './template-card/template-card.component';
import { TemplateListElementComponent } from './template-list-element/template-list-element.component';

export const TEMPLATE_SORT_VALUES = [
  { id: 'name', text: 'Name' },
  { id: 'version', text: 'Version' },
  { id: 'latest', text: 'Is Latest' }
] as const;

type TemplateSortValueId = (typeof TEMPLATE_SORT_VALUES)[number]['id'];

interface TemplateSearchFormValue {
  field: string | null;
  select: TemplateSortValueId | null;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

@Component({
  selector: 'app-template-list',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatTooltipModule,
    TemplateCardComponent,
    TemplateListElementComponent,
    RouterModule
  ],
  templateUrl: './template-list.component.html',
  styleUrls: ['./template-list.component.scss', '../../global-workspace-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateListComponent implements OnInit, OnDestroy {
  public searchForm = new FormGroup({
    field: new FormControl<string>(''),
    select: new FormControl<TemplateSortValueId | null>(null)
  });
  public readonly sortValues = TEMPLATE_SORT_VALUES;
  public isListView = false;
  private readonly sub = new Subscription();

  public static DEFAULT_PAGE_INDEX = 0;
  public static DEFAULT_PAGE_SIZE = 10;
  public static PAGE_SIZE_OPTIONS = [5, 10, 25, 100];

  public pageSizeOptions = TemplateListComponent.PAGE_SIZE_OPTIONS;
  public paginatorTrigger$ = new BehaviorSubject<PaginationState>({
    pageIndex: TemplateListComponent.DEFAULT_PAGE_INDEX,
    pageSize: TemplateListComponent.DEFAULT_PAGE_SIZE
  });

  public isLoading$: Observable<boolean>;
  private templatesData$: Observable<Template[]>;

  public searchFilteredTemplates$: Observable<Template[]>;
  public totalTemplatesLength$: Observable<number>;
  public pagedTemplates$: Observable<Template[]>;

  constructor(
    private readonly store: Store<AppState>,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.templatesData$ = this.store.select(templateFeature.selectData);
    this.isLoading$ = this.store.select(templateFeature.templatesIsLoading);

    this.searchFilteredTemplates$ = combineLatest([
      this.templatesData$,
      this.searchForm.valueChanges.pipe(startWith(this.searchForm.value))
    ]).pipe(
      map(([templates, form]) => {
        const field = form?.field ?? '';
        const select = form?.select ?? null;
        const filteredList = this.filterTemplates(templates, field);
        const onlylatest = filteredList.filter(template => template.is_latest);
        return this.sortTemplates(onlylatest, select);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.totalTemplatesLength$ = this.searchFilteredTemplates$.pipe(map(list => list.length));

    this.pagedTemplates$ = combineLatest([this.searchFilteredTemplates$, this.paginatorTrigger$.asObservable()]).pipe(
      map(([filteredList, paginator]) => {
        const startIndex = paginator.pageIndex * paginator.pageSize;
        const endIndex = startIndex + paginator.pageSize;
        return filteredList.slice(startIndex, endIndex);
      })
    );
  }

  ngOnInit(): void {
    this.store.dispatch(TemplateFetch({ show_latest: true }));

    this.sub.add(
      this.searchForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(300)).subscribe(filter => {
        this.onFilterChange(filter as TemplateSearchFormValue);
      })
    );

    setTimeout(() => this.initializeFormFromUrl(), 100);
  }

  private initializeFormFromUrl(): void {
    const params = this.route.snapshot.queryParams;
    const initialField = ((params['field'] || '') as TemplateSearchFormValue['field']) || null;
    const initialSelect = (params['select'] as TemplateSearchFormValue['select']) || null;

    const urlPageIndex = parseInt(params['page'], 10);
    const urlPageSize = parseInt(params['size'], 10);

    const pageIndex =
      !isNaN(urlPageIndex) && urlPageIndex >= 0 ? urlPageIndex : TemplateListComponent.DEFAULT_PAGE_INDEX;
    const pageSize =
      !isNaN(urlPageSize) && TemplateListComponent.PAGE_SIZE_OPTIONS.includes(urlPageSize)
        ? urlPageSize
        : TemplateListComponent.DEFAULT_PAGE_SIZE;

    const initialState: TemplateSearchFormValue = {
      field: initialField,
      select: initialSelect
    };

    this.paginatorTrigger$.next({ pageIndex, pageSize });
    this.searchForm.patchValue(initialState);
  }

  public onFilterChange(formValue: TemplateSearchFormValue): void {
    const newPageIndex = 0;
    this.paginatorTrigger$.next({ pageIndex: newPageIndex, pageSize: this.paginatorTrigger$.value.pageSize });
    this.updateUrl(formValue, newPageIndex, this.paginatorTrigger$.value.pageSize);
  }

  public handlePageEvent(event: PageEvent): void {
    this.updateUrl(this.searchForm.value as TemplateSearchFormValue, event.pageIndex, event.pageSize);
    this.paginatorTrigger$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  private updateUrl(formValue: TemplateSearchFormValue, pageIndex: number, pageSize: number): void {
    const queryParams: Record<string, string | null> = {};

    if (formValue.field) {
      queryParams['field'] = formValue.field;
    } else {
      delete queryParams['field'];
    }

    if (formValue.select) {
      queryParams['select'] = formValue.select;
    } else {
      delete queryParams['select'];
    }

    if (pageIndex !== TemplateListComponent.DEFAULT_PAGE_INDEX) {
      queryParams['page'] = pageIndex.toString();
    } else {
      delete queryParams['page'];
    }

    if (pageSize !== TemplateListComponent.DEFAULT_PAGE_SIZE) {
      queryParams['size'] = pageSize.toString();
    } else {
      delete queryParams['size'];
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace',
      replaceUrl: true
    });
  }

  private filterTemplates(templates: Template[], searchText?: string | null): Template[] {
    if (!searchText?.length) {
      return templates;
    }
    const normalisedSearchText = searchText.toLowerCase();
    return templates.filter(template => template.name.toLowerCase().includes(normalisedSearchText));
  }

  private sortTemplates(templates: Template[], sortOption?: TemplateSortValueId | null): Template[] {
    return [...templates].sort((a, b) => {
      if (sortOption === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortOption === 'version') {
        return a.version.localeCompare(b.version, undefined, { numeric: true, sensitivity: 'base' });
      } else if (sortOption === 'latest') {
        return Number(b.is_latest) - Number(a.is_latest);
      }
      return 0;
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
